import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { LOGO } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InstallNudge, PinnedWelcome } from "@/components/InstallAppCard";
import { WelcomePassCard } from "@/components/WelcomePassCard";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: "LayoutDashboard", end: true },
  { to: "/dashboard/explorers", label: "My Explorers", icon: "Users", testid: "tile-my-explorers" },
  { to: "/dashboard/book", label: "Book an Adventure", icon: "CalendarPlus", testid: "tile-book-adventure" },
  { to: "/dashboard/daytime", label: "Daytime Play", icon: "Sun", testid: "tile-daytime" },
  { to: "/dashboard/events", label: "Events", icon: "PartyPopper", testid: "tile-events" },
  { to: "/dashboard/camps", label: "Camps", icon: "Tent", testid: "tile-camps" },
  { to: "/dashboard/gallery", label: "Day Gallery", icon: "Image", testid: "tile-gallery" },
  { to: "/dashboard/bookings", label: "My Bookings", icon: "Ticket", testid: "tile-my-bookings" },
  { to: "/dashboard/credits", label: "Adventure Credits", icon: "Sparkles", testid: "tile-adventure-credits" },
  { to: "/dashboard/gold", label: "Gold Card", icon: "CreditCard", testid: "tile-gold-card" },
  { to: "/dashboard/wallet", label: "Adventure Wallet", icon: "Wallet", testid: "tile-adventure-wallet" },
  { to: "/dashboard/snacks", label: "Snack Shop", icon: "Cookie", testid: "tile-snack-shop" },
  { to: "/dashboard/dinner", label: "Dinner Club", icon: "UtensilsCrossed", testid: "tile-dinner-club" },
  { to: "/dashboard/lunch", label: "Camp Lunch", icon: "Sandwich", testid: "tile-lunch-club" },
  { to: "/dashboard/rock-talk", label: "Rock Talk", icon: "Languages", testid: "tile-rock-talk" },
  { to: "/dashboard/party", label: "Book a Party", icon: "PartyPopper", testid: "tile-party" },
  { to: "/dashboard/messages", label: "Messages", icon: "Megaphone", testid: "tile-messages" },
  { to: "/dashboard/documents", label: "Documents", icon: "FileText", testid: "tile-documents" },
  { to: "/dashboard/account", label: "Account", icon: "Settings", testid: "tile-account" },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6" data-testid="parent-nav-mission-control">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={LOGO} className="h-10 w-10 object-contain" alt="logo" />
          <div className="leading-none">
            <p className="font-display font-extrabold text-navy text-sm">MISSION CONTROL</p>
            <p className="text-[10px] text-turquoise tracking-widest font-semibold">ADVENTURE ROCK</p>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {(user?.role === "admin" || user?.role === "manager") && <Button variant="outline" size="sm" onClick={() => nav("/admin")} className="rounded-full" data-testid="goto-admin-btn">Admin</Button>}
          {(user?.role === "admin" || user?.role === "manager" || user?.role === "staff" || user?.role === "driver") && <Button variant="outline" size="sm" onClick={() => nav("/staff")} className="rounded-full" data-testid="goto-staff-btn">Scanner</Button>}
          <div className="hidden sm:block text-right"><p className="text-sm font-semibold text-navy">{user?.name}</p><p className="text-xs text-slate-400">{user?.email}</p></div>
          <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn"><Icons.LogOut className="w-5 h-5 text-slate-500" /></Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] p-4">
          <nav className="space-y-1">
            {NAV.map((n) => {
              const Icon = Icons[n.icon];
              return (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.testid || "overview"}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-turquoise/15 text-navy" : "text-slate-500 hover:bg-slate-100"}`}>
                  <Icon className="w-5 h-5" /> {n.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Mobile nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex overflow-x-auto pb-[env(safe-area-inset-bottom)]">
          {NAV.slice(1).map((n) => {
            const Icon = Icons[n.icon];
            return (
              <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.testid}`}
                className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 px-4 py-2 shrink-0 text-[10px] font-semibold ${isActive ? "text-turquoise" : "text-slate-400"}`}>
                <Icon className="w-5 h-5" /> {n.label.split(" ").pop()}
              </NavLink>
            );
          })}
        </nav>

        <main className="flex-1 p-4 sm:p-8 pb-24 lg:pb-8 max-w-6xl mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function OnMyWay() {
  const [kids, setKids] = useState([]);
  const [sel, setSel] = useState("");
  const [eta, setEta] = useState(null);
  useEffect(() => { api.get("/children").then((r) => { setKids(r.data); if (r.data[0]) setSel(r.data[0].id); }).catch(() => {}); }, []);
  const send = async (status, etaTime) => {
    if (!sel) return toast.error("Add an Explorer first.");
    try { const { data } = await api.post("/parent/arrival", { child_id: sel, status, eta: etaTime }); toast.success("Staff have been notified — thank you!"); if (data.fee_note) toast.info(data.fee_note, { duration: 8000 }); setEta(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send — please try again."); }
  };
  const startLate = () => { const d = new Date(Date.now() + 15 * 60000); setEta(d.toLocaleTimeString("en-GB", { timeZone: "Atlantic/Bermuda", hour: "2-digit", minute: "2-digit" })); };
  if (!kids.length) return null;
  return (
    <div className="bg-navy rounded-2xl p-5 mt-6 text-white" data-testid="onmyway-card">
      <p className="font-display font-bold flex items-center gap-2"><Icons.Car className="w-5 h-5 text-turquoise" /> Pickup status</p>
      <p className="text-white/60 text-sm mt-0.5">Let our team know you're coming for your Explorer. Pickup is by <b className="text-white">5:30 PM</b> — after that a late fee of <b className="text-gold">$5 per minute</b> applies, so please tell us if you're running late.</p>
      {kids.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {kids.map((k) => <button key={k.id} onClick={() => setSel(k.id)} data-testid={`onmyway-child-${k.id}`} className={`px-3 py-1.5 rounded-full text-sm ${sel === k.id ? "bg-turquoise text-navy font-semibold" : "bg-white/10 text-white/70"}`}>{k.first_name}</button>)}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <button onClick={() => send("on_way")} data-testid="onmyway-on-way" className="rounded-xl bg-turquoise/20 border border-turquoise/40 py-3 text-sm font-semibold">On my way</button>
        <button onClick={startLate} data-testid="onmyway-late" className="rounded-xl bg-amber-400/20 border border-amber-400/40 py-3 text-sm font-semibold">Running late</button>
        <button onClick={() => send("here")} data-testid="onmyway-here" className="rounded-xl bg-emerald-400/20 border border-emerald-400/40 py-3 text-sm font-semibold">I'm here</button>
      </div>
      {eta !== null && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-white/10 p-3" data-testid="late-eta-box">
          <span className="text-sm">I'll be there by</span>
          <input type="time" value={eta} onChange={(e) => setEta(e.target.value)} className="rounded-lg bg-white text-navy px-2 py-1 text-sm" data-testid="late-eta-input" />
          <button onClick={() => send("late", eta)} className="rounded-full bg-gold text-navy font-bold text-sm px-4 py-1.5" data-testid="late-eta-send">Tell staff</button>
          <button onClick={() => setEta(null)} className="text-xs text-white/50">Cancel</button>
        </div>
      )}
    </div>
  );
}

export function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ explorers: 0, credits: 0, bookings: 0 });
  const [messages, setMessages] = useState([]);
  const [phone, setPhone] = useState("441-519-1794");
  useEffect(() => {
    (async () => {
      try {
        const [kids, creds, bk, msg] = await Promise.all([
          api.get("/children"), api.get("/credits"), api.get("/bookings"), api.get("/messages"),
        ]);
        setStats({
          explorers: kids.data.length,
          credits: creds.data.filter((c) => c.status === "available").length,
          bookings: bk.data.filter((b) => b.status === "confirmed").length,
        });
        setMessages(msg.data.slice(0, 3));
      } catch (e) { console.error("Dashboard load failed:", e); }
    })();
    api.get("/public/settings").then((r) => { if (r.data.contact_phone) setPhone(r.data.contact_phone); }).catch(() => {});
  }, []);

  const tiles = NAV.slice(1);
  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-navy">Welcome back, {user?.name?.split(" ")[0]} 🧭</h1>
      <p className="text-slate-500">Your explorers' adventures, all in one place.</p>

      <InstallNudge />
      <WelcomePassCard />

      <div className="grid grid-cols-3 gap-3 mt-6">
        {[["Explorers", stats.explorers, "Users"], ["Credits", stats.credits, "Sparkles"], ["Booked Days", stats.bookings, "Ticket"]].map(([l, v, ic]) => {
          const Icon = Icons[ic];
          return (
            <div key={l} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`stat-${l.toLowerCase().replace(" ", "-")}`}>
              <Icon className="w-5 h-5 text-turquoise mb-2" />
              <p className="font-display font-extrabold text-2xl text-navy">{v}</p>
              <p className="text-xs text-slate-400">{l}</p>
            </div>
          );
        })}
      </div>

      <OnMyWay />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
        {tiles.map((t, i) => {
          const Icon = Icons[t.icon];
          return (
            <motion.div key={t.to} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <NavLink to={t.to} data-testid={t.testid} className="block bg-white rounded-2xl border border-slate-200 p-5 hover:border-turquoise hover:shadow-lg transition-all group">
                <div className="w-11 h-11 rounded-xl bg-navy/5 group-hover:bg-turquoise/15 flex items-center justify-center mb-3 transition-colors"><Icon className="w-6 h-6 text-navy" /></div>
                <p className="font-display font-bold text-navy text-sm">{t.label}</p>
              </NavLink>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8">
        <h2 className="font-display font-bold text-navy mb-3 flex items-center gap-2"><Icons.Megaphone className="w-5 h-5 text-coral" /> Announcements</h2>
        <div className="space-y-3">
          <PinnedWelcome />
          {messages.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`announcement-${m.id}`}>
              <p className="font-semibold text-navy">{m.title}</p>
              <p className="text-slate-500 text-sm mt-1">{m.body}</p>
            </div>
          ))}
          {messages.length === 0 && <p className="text-slate-400 text-sm">No announcements yet.</p>}
        </div>
      </div>

      <div className="mt-8 bg-navy rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-white" data-testid="dashboard-help-card">
        <div>
          <p className="font-display font-bold flex items-center gap-2"><Icons.LifeBuoy className="w-5 h-5 text-turquoise" /> Need a hand?</p>
          <p className="text-white/60 text-sm mt-0.5">Questions about bookings, camps or payments? We're happy to help.</p>
        </div>
        <a href={"tel:+1" + phone.replace(/[^0-9]/g, "")} data-testid="dashboard-call-btn" className="inline-flex items-center justify-center gap-2 bg-turquoise text-navy font-bold rounded-full px-5 py-2.5 text-sm hover:opacity-90 transition-opacity">
          <Icons.Phone className="w-4 h-4" /> Call {phone}
        </a>
      </div>
    </div>
  );
}
